import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { usuariosAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

const NIVELES = [
  { value: 0, label: 'Administrador' },
  { value: 1, label: 'Operador' },
  { value: 2, label: 'Chofer' },
];

const NIVEL_COLORS = { 0: 'error', 1: 'primary', 2: 'success' };

function getEmptyForm() {
  return { Nombre: '', User: '', Password: '', Nivel: 1 };
}

export default function Usuarios() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();

  const [openDialog, setOpenDialog] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, usuarioId: null, nombre: '' });

  const { data: usuarios, isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => {
      const response = await usuariosAPI.listar();
      return response.data.data;
    },
  });

  const crearMutation = useMutation({
    mutationFn: usuariosAPI.crear,
    onSuccess: () => {
      queryClient.invalidateQueries(['usuarios']);
      handleCloseDialog();
      showSnackbar('Usuario creado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al crear usuario', 'error');
    },
  });

  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }) => usuariosAPI.actualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['usuarios']);
      handleCloseDialog();
      showSnackbar('Usuario actualizado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al actualizar usuario', 'error');
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: usuariosAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries(['usuarios']);
      setDeleteConfirm({ open: false, usuarioId: null, nombre: '' });
      showSnackbar('Usuario eliminado exitosamente', 'success');
    },
    onError: (error) => {
      showSnackbar(error.response?.data?.error?.message || 'Error al eliminar usuario', 'error');
    },
  });

  const handleOpenDialog = (usuario = null) => {
    if (usuario) {
      setEditingUsuario(usuario);
      setFormData({
        Nombre: usuario.Nombre ?? '',
        User: usuario.User ?? '',
        Password: '',
        Nivel: usuario.Nivel ?? 1,
      });
    } else {
      setEditingUsuario(null);
      setFormData(getEmptyForm());
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUsuario(null);
    setFormData(getEmptyForm());
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.Nombre.trim()) errors.Nombre = 'El nombre es requerido';
    if (!formData.User.trim()) errors.User = 'El nombre de usuario es requerido';
    if (!editingUsuario && !formData.Password) errors.Password = 'La contraseña es requerida';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      nombre: formData.Nombre.trim(),
      user: formData.User.trim(),
      nivel: formData.Nivel,
    };
    if (formData.Password) payload.password = formData.Password;

    if (editingUsuario) {
      actualizarMutation.mutate({ id: editingUsuario.IdUsuario, data: payload });
    } else {
      crearMutation.mutate(payload);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  const isSelf = (usuario) => usuario.IdUsuario === currentUser?.id;
  const isMutating = crearMutation.isPending || actualizarMutation.isPending;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Usuarios</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Nuevo Usuario
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : usuarios && usuarios.length > 0 ? (
              usuarios.map((usuario) => (
                <TableRow key={usuario.IdUsuario}>
                  <TableCell>
                    {usuario.Nombre}
                    {isSelf(usuario) && (
                      <Chip label="Yo" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{usuario.User}</TableCell>
                  <TableCell>
                    <Chip
                      label={usuario.NivelLabel}
                      color={NIVEL_COLORS[usuario.Nivel] ?? 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(usuario)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={isSelf(usuario) ? 'No puede eliminar su propio usuario' : 'Eliminar'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isSelf(usuario)}
                          onClick={() => setDeleteConfirm({ open: true, usuarioId: usuario.IdUsuario, nombre: usuario.Nombre })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No hay usuarios registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de formulario */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre completo *"
                value={formData.Nombre}
                onChange={(e) => handleChange('Nombre', e.target.value)}
                error={!!formErrors.Nombre}
                helperText={formErrors.Nombre}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de usuario *"
                value={formData.User}
                onChange={(e) => handleChange('User', e.target.value)}
                error={!!formErrors.User}
                helperText={formErrors.User}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editingUsuario ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
                type="password"
                value={formData.Password}
                onChange={(e) => handleChange('Password', e.target.value)}
                error={!!formErrors.Password}
                helperText={formErrors.Password}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Rol *"
                value={formData.Nivel}
                onChange={(e) => handleChange('Nivel', e.target.value)}
              >
                {NIVELES.map((n) => (
                  <MenuItem key={n.value} value={n.value}>
                    {n.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isMutating}
          >
            {isMutating ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, usuarioId: null, nombre: '' })}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar al usuario <strong>{deleteConfirm.nombre}</strong>?
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, usuarioId: null, nombre: '' })}>
            Cancelar
          </Button>
          <Button
            onClick={() => eliminarMutation.mutate(deleteConfirm.usuarioId)}
            color="error"
            variant="contained"
            disabled={eliminarMutation.isPending}
          >
            {eliminarMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
